import { Box, SxProps, Theme } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getSidePanelScrollbarSx } from 'src/components/EntitySidePanel';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { useSiteTabs } from 'src/components/SidePanelTabs/SiteTab/use-site-tabs';
import { useAddressRecord } from 'src/graphql';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { SiteWindow } from 'src/pages/Sites/site';
import { useRegisterConversationModalDefaults } from 'src/pages/Sites/use-register-conversation-modal-defaults';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import useSiteViewsStore from 'src/store/site-views';
import { TabType, useEntityTabRoute, useGlobalTabs, useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

const mobileScrollerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden auto',
  ...getSidePanelScrollbarSx(),
};

const panelToolsSlotSx: SxProps<Theme> = {
  position: 'sticky',
  top: '0.5rem',
  zIndex: 3,
  height: 0,
  display: 'flex',
  // the slot has no height on purpose; centering would lift the tool above the cards
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  paddingRight: '1.125rem',
  pointerEvents: 'none',
  '& > *': { pointerEvents: 'auto' },
};

export const SiteExpanded = () => {
  const { showErrorAlert } = useNotifications();
  const { isMobile } = useScreenDimensions();
  const location = useLocation();
  const { navigate } = useNavigateRoute();
  const { recordId } = useRouteParams(UrlParams.recordId);
  const { openInTab } = useGlobalTabs();
  const { address, isLoading } = useAddressRecord({
    addressId: recordId,
  });

  const { selectedSiteTab, setSelectedSiteTab } = useSiteViewsStore();
  const { tabs, loading: tabsLoading } = useSiteTabs();

  const buildTabPath = useCallback(
    (slug: string) => portalPaths.site.full(recordId, slug),
    [recordId],
  );

  // the selected tab is part of the URL on the full page only; hold off while the
  // record or the permission-gated tab list is still resolving so a deep link to a
  // gated tab is not mistaken for an invalid one
  const { activeTab, setActiveTab } = useEntityTabRoute({
    tabs,
    loading: tabsLoading || isLoading || !address,
    savedTab: selectedSiteTab,
    buildPath: buildTabPath,
    onTabResolved: setSelectedSiteTab,
  });

  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const [panelToolsSlot, setPanelToolsSlot] = useState<HTMLElement | null>(null);

  const { setModalProps: setAddProjectModalProps } = useModalMethods(Modals.AddProject);

  useRegisterConversationModalDefaults(address);

  const customViewId = (location.state as { customViewId?: string })?.customViewId;

  useEffect(() => {
    let deleteAddProjectModalProps: (() => void) | undefined;
    if (currentPartnerId && address) {
      deleteAddProjectModalProps = setAddProjectModalProps({
        address,
      });
    }
    return () => {
      deleteAddProjectModalProps?.();
    };
  }, [currentPartnerId, setAddProjectModalProps, address]);

  const { customViews } = useCustomViews({
    entity: EntityNames.Site,
  });

  const customView = useMemo(
    () => (customViewId ? customViews.find((i) => i.customViewId === customViewId) : null),
    [customViews, customViewId],
  );

  // open expanded site in tab.
  useEffect(() => {
    if (!isLoading) {
      if (address) {
        openInTab(
          { type: TabType.Site, entity: address },
          { customViewId: customView?.customViewId },
        );
      } else {
        showErrorAlert(`Site "${recordId}" not found. You've been redirected to All Sites.`);
        navigate(
          portalPaths.entity.all({
            entityName: 'sites',
            customViewId: customView?.customViewId,
          }),
        );
      }
    }
  }, [
    customViewId,
    openInTab,
    isLoading,
    address,
    customView?.customViewId,
    showErrorAlert,
    recordId,
    navigate,
  ]);

  const onCloseSite = useCallback(() => {
    if (customView?.customViewId) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customView.customViewId,
          !isMobile ? recordId : undefined,
        ),
      );
    } else {
      navigate(
        portalPaths.entity.all({
          entityName: 'site',
          id: isMobile ? recordId : '',
        }),
      );
    }
  }, [customView?.customViewId, customView?.entityName, isMobile, navigate, recordId]);

  return address ? (
    <MetaTags title={address.fullAddress}>
      <Box height="100%" position="relative">
        <EntityWindowWrapper>
          {isMobile ? (
            <Box sx={mobileScrollerSx}>
              <Box ref={setPanelToolsSlot} sx={panelToolsSlotSx} />
              <Box sx={{ pt: '0.5rem', px: '0.5rem', pb: '0.5rem' }}>
                <SiteSidePanel site={address} toolsSlot={panelToolsSlot} />
              </Box>
            </Box>
          ) : (
            <SiteWindow
              isSidePanelExpanded
              site={address}
              onCloseSite={onCloseSite}
              activeTab={activeTab}
              onActiveTabChange={setActiveTab}
            />
          )}
        </EntityWindowWrapper>
      </Box>
    </MetaTags>
  ) : null;
};
